import React, { useState, useEffect, useMemo } from "react";
import Icon from "../../components/AppIcon";
import { arrivalNotificationAPI } from "../../utils/api";

export default function ArrivalNotificationModal({
  isOpen,
  onClose,
  application,
  workers,
  onSuccess,
}) {
  const [step, setStep] = useState(1); // 1: Select workers, 2: Set details, 3: Confirm
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [entryDate, setEntryDate] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Only approved workers can be selected
  const approvedWorkers = useMemo(() => {
    return workers.filter(
      (w) => String(w.status).toLowerCase() === "approved"
    );
  }, [workers]);

  // Filtered workers based on search
  const filteredWorkers = useMemo(() => {
    return approvedWorkers.filter((worker) => {
      const matchesSearch =
        !searchTerm ||
        worker.biodata?.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        worker.biodata?.passport_no
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
  }, [approvedWorkers, searchTerm]);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setStep(1);
      setSelectedWorkers([]);
      setEntryDate("");
      setRecipientEmail(application?.employer?.email || "");
      setError(null);
      setSearchTerm("");
    }
  }, [isOpen]);

  const handleSelectWorker = (workerId) => {
    setSelectedWorkers((prev) =>
      prev.includes(workerId)
        ? prev.filter((id) => id !== workerId)
        : [...prev, workerId]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredWorkers.map((w) => w.id);
    if (filteredIds.every((id) => selectedWorkers.includes(id))) {
      setSelectedWorkers((prev) =>
        prev.filter((id) => !filteredIds.includes(id))
      );
    } else {
      setSelectedWorkers((prev) => [
        ...new Set([...prev, ...filteredIds]),
      ]);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (selectedWorkers.length === 0) {
        setError("Please select at least one worker");
        return;
      }
      setError(null);
      setStep(2);
    } else if (step === 2) {
      if (!entryDate) {
        setError("Please select an entry date");
        return;
      }
      setError(null);
      setStep(3);
    }
  };

  const handleBack = () => {
    setError(null);
    setStep((prev) => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const selectedWorkerData = approvedWorkers
        .filter((w) => selectedWorkers.includes(w.id))
        .map((w) => ({
          applicationWorkerId: w.id,
          workerId: w.biodata_id || w.biodata?.id,
        }));

      const payload = {
        applicationId: application.id,
        entryDate,
        workers: selectedWorkerData,
        email: recipientEmail, // Pass email to create (just in case backend supports storing it)
      };

      // Step 1: Create notification
      const result = await arrivalNotificationAPI.create(payload);

      if (result.data) {
        // Step 2: Immediately send the email
        try {
          const notificationId = result.data.id;
          // Use the user-provided email if available, otherwise fallback to response email
          const emailToSend = recipientEmail || result.data.employerEmail;
          const sendResult = await arrivalNotificationAPI.send(notificationId, emailToSend);

          // Success - notification created AND email sent
          onSuccess?.(sendResult.data || result.data, "sent");
          onClose();
        } catch (sendErr) {
          console.error("Send notification email error:", sendErr);
          // Notification was created but email failed
          // Still close modal but show warning
          onSuccess?.(result.data, "created_but_send_failed",
            sendErr.response?.data?.error || "Email sending failed - notification saved as draft"
          );
          onClose();
        }
      }
    } catch (err) {
      console.error("Create notification error:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to create notification"
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const selectedWorkersData = approvedWorkers.filter((w) =>
    selectedWorkers.includes(w.id)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Icon name="Mail" size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">
                  Arrival Notification
                </h2>
                <p className="text-indigo-100 text-sm">
                  {application?.employer?.company_name || "Unknown Employer"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} className="text-white" />
            </button>
          </div>

          {/* Steps indicator */}
          <div className="flex items-center mt-4 space-x-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    s <= step
                      ? "bg-white text-indigo-600"
                      : "bg-white/30 text-white"
                  }`}
                >
                  {s < step ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    s
                  )}
                </div>
                {s < 3 && (
                  <div
                    className={`w-12 h-1 mx-1 rounded ${
                      s < step ? "bg-white" : "bg-white/30"
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex text-xs text-indigo-100 mt-2 space-x-2">
            <span className="w-8 text-center">Select</span>
            <span className="w-12" />
            <span className="w-8 text-center">Details</span>
            <span className="w-12" />
            <span className="w-8 text-center">Confirm</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center space-x-2">
              <Icon name="AlertCircle" size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: Select Workers */}
          {step === 1 && (
            <div>
              <div className="mb-4">
                <div className="relative">
                  <Icon
                    name="Search"
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by name or passport..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <Icon name="CheckSquare" size={16} />
                  <span>
                    {filteredWorkers.every((w) =>
                      selectedWorkers.includes(w.id)
                    )
                      ? "Deselect All"
                      : "Select All"}
                  </span>
                </button>
                <span className="text-sm text-gray-500">
                  {selectedWorkers.length} of {approvedWorkers.length} selected
                </span>
              </div>

              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Select
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        No
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Worker ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Passport
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredWorkers.map((worker, index) => (
                      <tr
                        key={worker.id}
                        className={`hover:bg-gray-50 ${
                          selectedWorkers.includes(worker.id)
                            ? "bg-indigo-50"
                            : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkers.includes(worker.id)}
                            onChange={() => handleSelectWorker(worker.id)}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {worker.biodata_id || worker.biodata?.id || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {worker.biodata?.full_name || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {worker.biodata?.passport_no || "N/A"}
                        </td>
                      </tr>
                    ))}
                    {filteredWorkers.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-8 text-center text-gray-500"
                        >
                          No approved workers found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Step 2: Entry Details */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Entry Time (Fixed)
                </label>
                <input
                  type="text"
                  value="3.00 - 5.00 Petang"
                  disabled
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pickup Location (Fixed)
                </label>
                <textarea
                  value="Blok B, Rooftop\nWestlite - PKNS Petaling Jaya\nJalan SS8/2 Sungai Way Free Trade Industrial Zone\n47300 Petaling Jaya\nSelangor Darul Ehsan"
                  disabled
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon
                    name="Info"
                    size={20}
                    className="text-blue-600 mt-0.5"
                  />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">Email will include:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Worker Listing PDF (auto-generated)</li>
                      <li>Employment Contract Template</li>
                      <li>Pickup Location Guide</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <Icon name="CheckCircle" size={24} className="text-green-600" />
                  <h3 className="text-lg font-semibold text-green-800">
                    Ready to Send
                  </h3>
                </div>
                <p className="text-green-700 text-sm">
                  Review the details below. Clicking "Create & Send Email" will immediately send the notification to the contractor.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">
                    Recipient Email <span className="text-red-500">*</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="Enter recipient email address..."
                      className="flex-1 px-3 py-1.5 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    />
                    <div className="text-xs text-gray-500 italic whitespace-nowrap">
                      (CC: arrivalnotify@clab.com.my)
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Company</div>
                  <div className="font-medium text-gray-900">
                    {application?.employer?.company_name || "-"}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Entry Date</div>
                  <div className="font-medium text-gray-900">
                    {entryDate
                      ? new Date(entryDate).toLocaleDateString("ms-MY", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "-"}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">Workers</div>
                  <div className="font-medium text-gray-900">
                    {selectedWorkers.length} worker(s)
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">VDR / Quota</div>
                  <div className="font-medium text-gray-900">
                    {application?.vdr_id || application?.quota_reference_no || "-"}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Selected Workers
                </h4>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          No
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Worker ID
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Name
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                          Passport
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedWorkersData.map((worker, index) => (
                        <tr key={worker.id}>
                          <td className="px-3 py-2">{index + 1}</td>
                          <td className="px-3 py-2">
                            {worker.biodata_id || worker.biodata?.id || "-"}
                          </td>
                          <td className="px-3 py-2 font-medium">
                            {worker.biodata?.full_name || "N/A"}
                          </td>
                          <td className="px-3 py-2">
                            {worker.biodata?.passport_no || "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex items-center justify-between">
          <div>
            {step > 1 && (
              <button
                onClick={handleBack}
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon name="ArrowLeft" size={18} />
                <span>Back</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            {step < 3 ? (
              <button
                onClick={handleNext}
                className="flex items-center space-x-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <span>Next</span>
                <Icon name="ArrowRight" size={18} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? (
                  <>
                    <Icon name="Loader" size={18} className="animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={18} />
                    <span>Create & Send Email</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
